import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../api/useApi';
import type { DocListEntry, DocResponse } from '../api/client';
import {
  MIN_POSTER_HEIGHT,
  framedPosterHeight,
  POSTER_HEIGHT_MESSAGE,
  POSTER_SANDBOX,
  usePosterColorScheme,
  withPosterMeasureScript,
} from '../lib/poster';

/**
 * The Docs section: a wiki-style browser over almanac-wiki's `docs/` tree of
 * explainer documents. A left sidebar lists every doc grouped by its nested
 * category path; the selected doc's self-contained HTML renders in the same
 * sandboxed, height-synced iframe the research poster uses, with the doc's
 * cross-links (feature package, Linear, PRs, related docs) shown as page
 * chrome around it so the HTML itself stays portable.
 */

type ListState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; docs: DocListEntry[] };

type DocState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; doc: DocResponse };

/** localStorage key remembering whether draft and archived docs are listed. */
const SHOW_UNPUBLISHED_KEY = 'almanac.docs.show-unpublished';

/** Docs hidden from the sidebar unless the reader opts in. */
function isUnpublished(doc: DocListEntry): boolean {
  return doc.status === 'draft' || doc.status === 'archived';
}

/**
 * The docs the sidebar lists. Draft and archived docs are dropped unless
 * `showUnpublished` is set, except the doc being read: a direct link to a
 * hidden doc still shows where it sits in the tree.
 */
function visibleDocs(
  docs: DocListEntry[],
  showUnpublished: boolean,
  activeSlug: string | undefined,
): DocListEntry[] {
  if (showUnpublished) return docs;
  return docs.filter((doc) => !isUnpublished(doc) || doc.slug === activeSlug);
}

/** One level of the sidebar tree: docs at this level plus nested categories. */
interface CategoryNode {
  docs: DocListEntry[];
  children: Map<string, CategoryNode>;
}

function buildCategoryTree(docs: DocListEntry[]): CategoryNode {
  const root: CategoryNode = { docs: [], children: new Map() };
  for (const doc of docs) {
    let node = root;
    for (const segment of doc.category) {
      let child = node.children.get(segment);
      if (!child) {
        child = { docs: [], children: new Map() };
        node.children.set(segment, child);
      }
      node = child;
    }
    node.docs.push(doc);
  }
  return root;
}

function CategoryBranch({
  name,
  node,
  activeSlug,
  depth,
}: {
  name: string | null;
  node: CategoryNode;
  activeSlug: string | undefined;
  depth: number;
}) {
  // The category name sits at the same indent as its sibling docs; only the
  // category's own docs and sub-categories step in, so a nested category reads
  // as a peer group under its parent rather than a child of the last doc.
  return (
    <div>
      {name !== null && (
        <div className="px-2 pt-3 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {name}
        </div>
      )}
      <div className={depth > 1 ? 'ml-3 border-l border-border/60 pl-3' : undefined}>
        <ul>
          {node.docs.map((doc) => (
            <li key={doc.slug}>
              <Link
                to={`/docs/${doc.slug}`}
                aria-current={doc.slug === activeSlug ? 'page' : undefined}
                className={
                  doc.slug === activeSlug
                    ? 'block rounded-md bg-muted px-2 py-1.5 text-sm font-medium text-foreground'
                    : 'block rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground'
                }
              >
                {doc.title}
                {doc.status !== 'active' ? (
                  <span className="ml-2 rounded border border-border px-1 py-px text-[0.625rem] text-muted-foreground uppercase">
                    {doc.status}
                  </span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
        {[...node.children.entries()].map(([childName, child]) => (
          <CategoryBranch
            key={childName}
            name={childName}
            node={child}
            activeSlug={activeSlug}
            depth={depth + 1}
          />
        ))}
      </div>
    </div>
  );
}

/** API error bodies are raw JSON (e.g. {"error":"…"}); surface the human part. */
function errorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const parsed: unknown = JSON.parse(raw);
    const message = (parsed as { error?: unknown }).error;
    if (typeof message === 'string') return message;
  } catch {
    // not a JSON body — show it as-is
  }
  return raw;
}

function githubPrUrl(ref: string): string | null {
  const match = /^([\w.-]+\/[\w.-]+)#(\d+)$/.exec(ref);
  if (!match) return null;
  return `https://github.com/${match[1]}/pull/${match[2]}`;
}

function linearIssueUrl(key: string): string {
  return `https://linear.app/the-langston-co/issue/${key}`;
}

const RELATED_LINK_CLASS =
  'rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground';

const DOC_LINK_MESSAGE = 'almanac-doc-link';
// Keep this identical to api/src/domain/docs.ts: DOC_SLUG.
const DOC_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,127}$/u;
const EXTERNAL_LINK_BASE = '<base target="_blank">';

/**
 * Make links inside an opaque-origin explainer useful without weakening its
 * isolation. External links get a browser-level target; links to another
 * Almanac explainer ask the parent SPA to navigate in place.
 */
function withDocLinkHandling(html: string, appOrigin: string): string {
  const withBase = /<head(?:\s[^>]*)?>/iu.test(html)
    ? html.replace(/<head(?:\s[^>]*)?>/iu, (head) => `${head}${EXTERNAL_LINK_BASE}`)
    : html.replace(/<html(?:\s[^>]*)?>/iu, (root) => `${root}<head>${EXTERNAL_LINK_BASE}</head>`);
  const origin = JSON.stringify(appOrigin);

  return `${withBase}<script>
(function () {
  var appOrigin = ${origin};
  var canonicalOrigin = 'https://almanac.thelangstonco.com';

  function internalDocSlug(anchor) {
    try {
      var url = new URL(anchor.href);
      if (url.origin !== appOrigin && url.origin !== canonicalOrigin) return null;
      var match = /^\\/docs\\/([^/]+)\\/?$/.exec(url.pathname);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  }

  function prepare(anchor) {
    if (internalDocSlug(anchor) !== null) return;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  }

  document.querySelectorAll('a[href]').forEach(prepare);
  document.addEventListener('click', function (event) {
    var target = event.target;
    var anchor = target && target.closest ? target.closest('a[href]') : null;
    if (!anchor) return;
    var slug = internalDocSlug(anchor);
    if (slug !== null) {
      event.preventDefault();
      parent.postMessage({ type: '${DOC_LINK_MESSAGE}', slug: slug }, appOrigin);
      return;
    }
    prepare(anchor);
  });
})();
</script>`;
}

function RelatedLinks({ doc }: { doc: DocResponse }) {
  const { related } = doc.meta;
  const prLinks = (related.prs ?? [])
    .map((ref) => ({ ref, url: githubPrUrl(ref) }))
    .filter((entry): entry is { ref: string; url: string } => entry.url !== null);
  const hasAny =
    related.package ||
    prLinks.length > 0 ||
    (related.linear?.length ?? 0) > 0 ||
    (related.docs?.length ?? 0) > 0;
  if (!hasAny) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {related.package ? (
        <Link to={`/briefing/packages/${related.package}`} className={RELATED_LINK_CLASS}>
          Package: {related.package.replace(/^pkg-/, '')}
        </Link>
      ) : null}
      {(related.linear ?? []).map((key) => (
        <a
          key={key}
          href={linearIssueUrl(key)}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={RELATED_LINK_CLASS}
        >
          {key}
        </a>
      ))}
      {prLinks.map(({ ref, url }) => (
        <a
          key={ref}
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={RELATED_LINK_CLASS}
        >
          {ref}
        </a>
      ))}
      {(related.docs ?? []).map((slug) => (
        <Link key={slug} to={`/docs/${slug}`} className={RELATED_LINK_CLASS}>
          {slug}
        </Link>
      ))}
    </div>
  );
}

export function DocsPage() {
  const { docSlug } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const posterColorScheme = usePosterColorScheme();
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [docState, setDocState] = useState<DocState>({ status: 'idle' });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [docHeight, setDocHeight] = useState(MIN_POSTER_HEIGHT);
  const [showUnpublished, setShowUnpublished] = useState(() => {
    try {
      return localStorage.getItem(SHOW_UNPUBLISHED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_UNPUBLISHED_KEY, String(showUnpublished));
    } catch {
      // Storage can be unavailable (private mode); the toggle still works for the session.
    }
  }, [showUnpublished]);

  useEffect(() => {
    api
      .listDocs()
      .then((data) => setList({ status: 'loaded', docs: data.docs }))
      .catch((e: unknown) => setList({ status: 'error', message: errorMessage(e) }));
  }, [api]);

  useEffect(() => {
    if (!docSlug) {
      setDocState({ status: 'idle' });
      return;
    }
    // Guard against overlapping requests: a late response for a previous
    // slug must not overwrite the current route's state.
    let stale = false;
    setDocState({ status: 'loading' });
    setDocHeight(MIN_POSTER_HEIGHT);
    api
      .getDoc(docSlug)
      .then((doc) => (stale ? undefined : setDocState({ status: 'loaded', doc })))
      .catch((e: unknown) =>
        stale ? undefined : setDocState({ status: 'error', message: errorMessage(e) }),
      );
    return () => {
      stale = true;
    };
  }, [api, docSlug]);

  // Messages must come from THIS iframe. Height reports can influence layout;
  // explainer-link requests can only navigate to a syntactically valid doc
  // route. The API remains authoritative for whether that slug exists, so doc
  // navigation does not race the independently loaded sidebar index.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const payload: unknown = event.data;
      if (typeof payload !== 'object' || payload === null) return;
      if ((payload as { type?: unknown }).type === DOC_LINK_MESSAGE) {
        const { slug } = payload as { slug?: unknown };
        if (typeof slug === 'string' && DOC_SLUG_RE.test(slug)) {
          void navigate(`/docs/${encodeURIComponent(slug)}`);
        }
        return;
      }
      if ((payload as { type?: unknown }).type !== POSTER_HEIGHT_MESSAGE) return;
      const height = framedPosterHeight((payload as { height?: unknown }).height);
      if (height === null) return;
      setDocHeight(height);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate]);

  const srcDoc = useMemo(() => {
    if (docState.status !== 'loaded') return null;
    return withPosterMeasureScript(withDocLinkHandling(docState.doc.html, window.location.origin));
  }, [docState]);

  const listed = list.status === 'loaded' ? visibleDocs(list.docs, showUnpublished, docSlug) : [];
  const hiddenCount = list.status === 'loaded' ? list.docs.length - listed.length : 0;

  if (list.status === 'loaded' && !docSlug && listed.length > 0) {
    return <Navigate to={`/docs/${listed[0]?.slug}`} replace />;
  }

  const tree = list.status === 'loaded' ? buildCategoryTree(listed) : null;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row">
      <aside className="lg:w-60 lg:shrink-0" aria-label="Docs">
        <div className="lg:sticky lg:top-20">
          <div className="px-2 pb-2 text-sm font-semibold text-foreground">Docs</div>
          {list.status === 'loading' && (
            <p className="px-2 text-sm text-muted-foreground">Loading…</p>
          )}
          {list.status === 'error' && <p className="px-2 text-sm text-danger">{list.message}</p>}
          {list.status === 'loaded' && list.docs.length === 0 && (
            <p className="px-2 text-sm text-muted-foreground">No docs published yet.</p>
          )}
          {tree ? <CategoryBranch name={null} node={tree} activeSlug={docSlug} depth={1} /> : null}
          {list.status === 'loaded' && (showUnpublished || hiddenCount > 0) ? (
            <label className="mt-4 flex cursor-pointer items-center gap-2 px-2 text-xs text-muted-foreground hover:text-foreground">
              <input
                type="checkbox"
                checked={showUnpublished}
                onChange={(event) => setShowUnpublished(event.target.checked)}
                className="size-3.5 accent-primary"
              />
              <span>
                Show drafts and archived
                {hiddenCount > 0 ? ` (${hiddenCount})` : ''}
              </span>
            </label>
          ) : null}
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        {docState.status === 'loading' && (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
        )}
        {docState.status === 'error' && (
          <p className="py-12 text-center text-sm text-danger">{docState.message}</p>
        )}
        {docState.status === 'loaded' && srcDoc !== null && (
          <article>
            <header className="mb-4 flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h1 className="text-lg font-semibold text-foreground">{docState.doc.meta.title}</h1>
                <span className="text-xs text-muted-foreground">
                  Updated {docState.doc.meta.updated}
                </span>
              </div>
              <RelatedLinks doc={docState.doc} />
            </header>
            <div className="overflow-hidden rounded-lg border border-border">
              {/*
                SECURITY (load-bearing): explainer docs are agent-authored HTML
                rendered in a sandboxed iframe WITHOUT allow-same-origin — a
                unique opaque origin with no reach into the parent DOM, cookies,
                or OAuth tokens. Popups deliberately escape this sandbox so
                external links open in their normal browser context; generated
                links sever window.opener. NEVER add allow-same-origin. (See
                POSTER_SANDBOX in web/src/lib/poster.ts.)
              */}
              <iframe
                ref={iframeRef}
                title={docState.doc.meta.title}
                srcDoc={srcDoc}
                sandbox={POSTER_SANDBOX}
                className="block w-full border-0"
                style={{ height: `${docHeight}px`, colorScheme: posterColorScheme }}
              />
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
